/**
 * EIS-1124 PART A -- rep-facing "Possible duplicates" panel, mounted on the
 * baseball card's Details tab directly under the "Data Source" section.
 *
 * Nothing renders, and no fetch fires, unless store.user?.earlyRelease is
 * true and store.currentPractitioner?.id is set (Layer 2 of the entity-
 * resolution regression gate: rep-facing matching UI gates on
 * store.user.earlyRelease). See PossibleDuplicates.test.tsx.
 *
 * PART B (turtle_api, a separate loop) implements GET
 * /api/matching/for_practitioner and the propose/dismiss routes to the fixed
 * CONTRACT in ./types; there is no live backend yet.
 */
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { logEvent } from '@amplitude/analytics-browser';
import { store } from 'stores/Store';
import detailsStyles from 'Modules/PractitionerBaseballCard/modules/Details/Details.module.scss';
import styles from './PossibleDuplicates.module.scss';
import usePossibleDuplicates from './usePossibleDuplicates';
import { CandidateRow, buildCandidateRows, chipForState } from './helpers';
import { Group, Member, Suggestion } from './types';

// Mirrors the local trackClick pattern used elsewhere (LeadForm.tsx,
// Sidepane/index.tsx) rather than adding a store field for telemetry.
const trackClick = (label: string) => {
  logEvent(label, { section: label });
};

const pillClassName = (variant: CandidateRow['pillVariant']): string =>
  [
    styles.channel,
    variant === 'direct' ? styles.channelDirect : '',
    variant === 'distributor' ? styles.channelDistributor : '',
  ]
    .filter(Boolean)
    .join(' ');

const SourcePill = ({ label, variant }: { label: string; variant: CandidateRow['pillVariant'] }) => (
  <span className={pillClassName(variant)}>{label}</span>
);

const CandidateRowView = ({ row, showScoreTier }: { row: CandidateRow; showScoreTier: boolean }) => (
  <div className={styles.candidateRow}>
    <span className={styles.candidateName}>{row.displayName}</span>
    <SourcePill label={row.pillLabel} variant={row.pillVariant} />
    {row.cityState && <span className={styles.candidateMeta}>{row.cityState}</span>}
    {showScoreTier && (
      <>
        <span className={styles.candidateMeta}>{row.scorePercent}</span>
        <span className={styles.candidateMeta}>{row.tier}</span>
      </>
    )}
  </div>
);

const groupMemberRows = (members: Member[], currentPractitionerId: number): CandidateRow[] =>
  buildCandidateRows({ members }, currentPractitionerId);

const chipClassName = (kind: 'verified' | 'pending' | 'suggestions'): string => {
  if (kind === 'verified') return `${styles.stateChip} ${styles.stateChipVerified}`;
  if (kind === 'pending') return `${styles.stateChip} ${styles.stateChipPending}`;
  return `${styles.stateChip} ${styles.stateChipSuggestions}`;
};

const PossibleDuplicates = observer(() => {
  const practitionerId: number | undefined = store.currentPractitioner?.id;
  const enabled = Boolean(store.user?.earlyRelease) && practitionerId != null;
  const { data, loading, error, propose, dismiss } = usePossibleDuplicates(practitionerId, enabled);

  const [actioningId, setActioningId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const openedTracked = useRef(false);
  // EIS-1124 part A round 4 (Codex finding): `actioningId` state is what the
  // render reads (disabled attributes, aria-busy), but handleYes/handleNo
  // close over the render-time value of it, and setState is not synchronous
  // from a handler's point of view -- two invocations that both fire before
  // React commits the first one's update (e.g. two clicks batched into one
  // React 18 event flush) can both still see the STALE, pre-update
  // `actioningId` and both pass the `if (actioningId !== null) return;`
  // check, starting two concurrent mutations. A ref doesn't have that
  // problem: `mutationLockRef.current = true` mutates the SAME object every
  // closure reads, immediately, with no render/commit in between -- so it's
  // checked and set at the very top of both handlers, before any await or
  // setState, and is the thing that actually makes the guard atomic. See
  // PossibleDuplicates.test.tsx's round-4 test.
  const mutationLockRef = useRef(false);

  useEffect(() => {
    if (enabled && !openedTracked.current) {
      trackClick('Possible duplicates panel opened');
      openedTracked.current = true;
    }
  }, [enabled]);

  if (!enabled) return null;

  // EIS-1124 part A round 3, finding 2: a suggestion action is a no-op
  // whenever ANY suggestion is already in flight -- not just when it's this
  // exact suggestion. `disabled` on the buttons enforces this in the DOM,
  // but the handlers guard the same condition themselves so a click that
  // reaches here some other way (bypassed disabled attribute, a queued event
  // handler) can't start a second concurrent mutation/refetch. Round 4: the
  // guard that actually makes this atomic is `mutationLockRef`, checked and
  // set BEFORE any await or setState -- see its declaration above.
  // `actioningId` stays exactly as it was for rendering (disabled/aria-busy).
  const handleYes = async (suggestion: Suggestion) => {
    if (mutationLockRef.current) return;
    // Round 4 checker LOW nit: acquire the lock as the very FIRST statement
    // inside `try` (still before any `await`, so atomicity is unchanged --
    // no other synchronous code runs between the check above and this
    // assignment) rather than before `try` opens. If a synchronous
    // exception fired between the old acquisition point and `try`
    // (`setActioningId`, `setActionError`, or `trackClick`'s `logEvent`),
    // the lock would be acquired and never released by `finally`, silently
    // bricking the panel forever. Now every acquisition is covered by the
    // same `finally` that releases it.
    try {
      mutationLockRef.current = true;
      setActioningId(suggestion.id);
      setActionError(null);
      trackClick('Possible duplicates: Yes');
      await propose(suggestion);
    } catch (err) {
      console.error('Error proposing possible-duplicate match:', err);
      setActionError('Failed to confirm this match. Please try again.');
    } finally {
      mutationLockRef.current = false;
      setActioningId(null);
    }
  };

  const handleNo = async (suggestion: Suggestion) => {
    if (mutationLockRef.current) return;
    // See handleYes above for why the acquisition moved inside `try`.
    try {
      mutationLockRef.current = true;
      setActioningId(suggestion.id);
      setActionError(null);
      trackClick('Possible duplicates: No');
      await dismiss(suggestion);
    } catch (err) {
      console.error('Error dismissing possible-duplicate suggestion:', err);
      setActionError('Failed to dismiss this suggestion. Please try again.');
    } finally {
      mutationLockRef.current = false;
      setActioningId(null);
    }
  };

  const chip = data ? chipForState(data) : null;

  const verifiedGroup: Group | null =
    data?.active_group && ['verified', 'ready_to_send', 'synced', 'sync_failed'].includes(data.active_group.status)
      ? data.active_group
      : null;

  const pendingGroups: Group[] = data
    ? data.my_pending.length > 0
      ? data.my_pending
      : data.active_group && data.active_group.status === 'proposed'
      ? [data.active_group]
      : []
    : [];

  const suggestions = data?.suggestions || [];

  const nothingToShow =
    !loading && !error && !verifiedGroup && pendingGroups.length === 0 && suggestions.length === 0;

  return (
    <>
      <div className={detailsStyles.sectionWrapper}>
        <p className={detailsStyles.headerText}>
          Possible duplicates
          {chip && <span className={chipClassName(chip.kind)}>{chip.label}</span>}
        </p>
      </div>
      <div className={styles.body}>
        {loading && <p className={styles.emptyState}>Loading…</p>}
        {error && <p className={styles.inlineError}>{error}</p>}
        {actionError && <p className={styles.inlineError}>{actionError}</p>}

        {verifiedGroup && (
          <div className={styles.groupSection}>
            {groupMemberRows(verifiedGroup.members, practitionerId as number).map((row) => (
              <CandidateRowView key={row.memberId} row={row} showScoreTier={false} />
            ))}
          </div>
        )}

        {pendingGroups.map((group) => (
          <div className={styles.groupSection} key={group.id}>
            {groupMemberRows(group.members, practitionerId as number).map((row) => (
              <CandidateRowView key={row.memberId} row={row} showScoreTier={false} />
            ))}
          </div>
        ))}

        {suggestions.map((suggestion) => {
          const rows = buildCandidateRows(suggestion, practitionerId as number);
          // isActioning identifies the ONE row actually in flight (kept for
          // its own per-row indicator, e.g. aria-busy below); actionsLocked
          // is whether ANY row is in flight, which is what gates every
          // button -- see finding 2.
          const isActioning = actioningId === suggestion.id;
          const actionsLocked = actioningId !== null;
          return (
            <div className={styles.suggestionBlock} key={suggestion.id}>
              {rows.map((row) => (
                <CandidateRowView key={row.memberId} row={row} showScoreTier />
              ))}
              <div className={styles.suggestionActions}>
                <button
                  type="button"
                  className={styles.yesButton}
                  disabled={actionsLocked}
                  aria-busy={isActioning}
                  onClick={() => handleYes(suggestion)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={styles.noButton}
                  disabled={actionsLocked}
                  aria-busy={isActioning}
                  onClick={() => handleNo(suggestion)}
                >
                  No
                </button>
              </div>
            </div>
          );
        })}

        {nothingToShow && <p className={styles.emptyState}>No possible duplicates found.</p>}
      </div>
    </>
  );
});

export default PossibleDuplicates;
